import { Component, createRef } from 'react';
import Router from 'next/router';
import { debounceTime } from 'rxjs';

import { container, lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { PageDataProviderService } from '../../../services/page-data-provider.service';
import { SettingsService } from '../../../services/settings.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { WebSocketService } from '../../../services/websocket.service';
import { FileUploadUtils } from '../../../utils/file-upoad.utils';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { goToUploadAgain, prepareUploadAgain, X2_COPY } from '../../../utils/upload-expiry';
import { merge } from '../../../utils/lodash-min';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { AdvancedOptionsSectionComponent } from '../advanced-options-section/advanced-options-section.component';
import { ConvertOptionsContextV2, ConvertOptionsSummary } from '../../convert-options-v2/convert-options.context';
import { applyFeatureFlagGate, optionTreeFlags } from '../../convert-options-v2/pdf-compress-presets.gate';
import { FileProcessEditorComponent, UploadExpiredEvent } from '../../converter/file-process-editor.component';
import { UpdateProcessProbedDataFnProvider } from '../file-card/file-card.component';
import Modal from '../modal/modal.component';
import { COMPRESS_OUTCOME_COPY, fmtOutcomeCopy } from '../file-list/compress-outcome';
import { suggestedGroupIds, type MakeSmallerSuggestion } from '../file-list/make-smaller-suggestion';
import * as styles from './file-upload-reedit-modal.component.module.scss';
import { ReeditAlert, ReeditFooterMode, ReeditSuggestion, resolveReeditFooterMode } from './reedit-footer-mode';
// FIX-8 duplicate submissions (UX-SPEC.md sections 8.2-8.4).
import { reeditRerunState, ReeditRerunState, rerunDedupeOfferEnabled } from '../../converter/rerun/rerun-offer';
import { nextRerunRegionId, RERUN_CARD_CHANGE_DEBOUNCE_MS, rerunContext } from '../../converter/rerun/rerun-context';
import { rerunCopy } from '../../converter/rerun/rerun-copy';
import { canDownloadExistingResult, downloadExistingResult } from '../../converter/rerun/download-existing';
import { revealInScrollParent } from './reveal-in-scroll-parent';

const RE_EDITED_FILES = 'RE_EDITED_FILES';


interface FileUploadReEditModalComponentProps {
    fileUpload: FileUpload;
    /**
     * Prod fixes 2026-10-02 (PLAN.md C6): FIX-3's "Make it smaller" opens the modal with one stronger option
     * preselected (T5 applies it and says so in the status line). Absent = today's modal. T5's
     * `MakeSmallerSuggestion` adds `patch`, merged over the card's options (every key Apply must change).
     */
    suggestion?: ReeditSuggestion & Partial<Pick<MakeSmallerSuggestion, 'patch' | 'rule'>>;
    /** C6: the card's own Download path, for FIX-8's unchanged-success footer (T10). Absent = today's modal. */
    onDownloadExisting?: () => void;
}

interface FileUploadReEditModalComponentState {
    /** C6: an inline refusal at the top of the body (`role="alert"`, focused, scrolled into view). */
    alert: ReeditAlert | null;
    /** C6: one polite line above the options (`role="status"`), e.g. X3-B2. */
    statusLine: string | null;
    /** C6: FIX-2 (T3) -- the upload is gone; selects the `expired` footer. */
    expired: boolean;
}

const NOT_UNCHANGED: ReeditRerunState = { running: false, unchangedSucceeded: false, prior: null };

interface ExtraProps {
    convertOptionsContext: ConvertOptionsContextV2;
    uploadInProgressFiles: UploadInProgressFile<File>[];
    onOptionChange: (data: ConvertOptionsSummary) => void;
}

export class FileUploadReEditModalComponent extends Component<FileUploadReEditModalComponentProps, FileUploadReEditModalComponentState> implements UpdateProcessProbedDataFnProvider {
    private readonly logger = Logger.getLogger('FileUploadReEditModalComponent');

    readonly state: Readonly<FileUploadReEditModalComponentState> = { alert: null, statusLine: null, expired: false };

    modalRef = createRef<Modal>();
    editorRef = createRef<FileProcessEditorComponent>();
    alertRef = createRef<HTMLDivElement>();
    /** The alert most recently moved into view, so a re-render does not steal focus again. */
    private focusedAlert: ReeditAlert | null = null;
    bodyRef = createRef<HTMLDivElement>();
    /** FIX-3b (UX1-07): the top-level groups holding the suggested setting, and whether the shown one was revealed. */
    suggestedGroupIds: string[] = [];
    private suggestionRevealState: 'pending' | 'done' = 'pending';
    uploadInProgressFiles: UploadInProgressFile<File>[] = [];
    options: any;
    extraProps: ExtraProps;

    @lazyInject(FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(FileUploadService) fileUploadService: FileUploadService;
    @lazyInject(WebSocketService) websocketService: WebSocketService;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;
    @lazyInject(PageDataProviderService) optionsService: PageDataProviderService;
    @lazyInject(SpinnerService) spinnerService: SpinnerService;
    @lazyInject(ScriptLoadService) scriptLoadService: ScriptLoadService;
    @lazyInject(SettingsService) settingsService: SettingsService;

    constructor(props) {
        super(props);
    }

    async componentDidMount() {
        const instance = await this.spinnerService.show('Loading....');
        this.uploadInProgressFiles = await FileUploadUtils.toUploadInProgressFile(this.props.fileUpload);
        const options = this.props.fileUpload.optionsSnapshot;
        this.optionsService.loadConvertOptions(this.props.fileUpload.uniqueProcessType).subscribe({
            next: (functionalOptions) => {
                this.options = merge(functionalOptions, options);

                const ctx = ConvertOptionsContextV2.newContext(this.options);
                if (this.props.fileUpload.options) {
                    ctx.applySummary(this.props.fileUpload.options);
                }
                // FIX-3b: the suggestion is a patch over the run's own options, so everything else the user chose stays.
                if (this.props.suggestion?.patch) {
                    ctx.applySummary({ ...(this.props.fileUpload.options || {}), ...this.props.suggestion.patch });
                    this.suggestedGroupIds = suggestedGroupIds(ctx, this.props.suggestion.optionKey);
                }
                // Same gate as the converter page (pdf-compress-presets.gate.ts): hidden until the
                // flag says true. A re-edit of a job run on a now-hidden level falls back to the
                // default level, because a hidden level is never summarized.
                applyFeatureFlagGate(ctx, optionTreeFlags(null));
                this.settingsService?.featureFlags?.pipe(untilDestroyed(this)).subscribe({
                    next: (flags) => {
                        if (applyFeatureFlagGate(ctx, optionTreeFlags(flags))) {
                            ctx.emitModelChanged();
                        }
                        const rerunOn = rerunDedupeOfferEnabled(flags);
                        if (rerunOn !== this.rerunEnabled) {
                            this.rerunEnabled = rerunOn;
                            this.refreshRerun();
                        }
                    },
                });
                // FIX-8: a card appearing or going changes the footer...
                this.fileManagerService?.fileUploadsListChanged?.pipe(untilDestroyed(this)).subscribe({
                    next: () => this.refreshRerun(),
                });
                // ...and so does one finishing or failing (running -> done / failed): that is a per-card change, which
                // `fileUploadsListChanged` (add/remove only) never reports (fu-t10 F2). Debounced: progress frames too.
                this.fileManagerService?.fileUploadChanged?.pipe(debounceTime(RERUN_CARD_CHANGE_DEBOUNCE_MS), untilDestroyed(this)).subscribe({
                    next: () => this.refreshRerun(),
                });
                ctx.modelChangeObserver.pipe(untilDestroyed(this)).subscribe({
                    next: (summary: ConvertOptionsSummary) => {
                        this.convertOptionCategoriesChanged(summary);
                    }
                });

                this.extraProps = {
                    convertOptionsContext: ctx,
                    uploadInProgressFiles: this.uploadInProgressFiles,
                    onOptionChange: (data) => {
                        this.logger.debug({ message: `Data from ConvertOptionsComponent `, data });
                    },
                };
                this.logger.debug({ message: "Upload in-progress files", data: this.uploadInProgressFiles });
                this.spinnerService.hide(instance);
                this.forceUpdate();
            },
            error: (err) => {
                this.spinnerService.hide(instance);
                this.uiContext.handleError(err);
            }
        })

        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh
        // this.optionsService.loadConvertOptions(this.props).subscribe({
        //     next: (functionalOptions) => {
        //         this.options = merge(functionalOptions, options);
        //     }
        // });
        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh

        // this.optionsService.getOptionsFor(this.fileUpload.uniqueProcessType);

    }

    /**
     * FIX-3b (UX1-07): once the options are on screen, scroll the modal body (only it) just enough to show the suggested
     * setting -- its control in the marked group, else the group -- which at 390 px is below the fold; while that allows,
     * the X3-B2 line stays fully in view too. Once per modal: a later re-render (the user picking something else) never
     * pulls the body back. The group is looked up for a few frames, as the options render after the context is ready; if
     * it never shows (a hidden group), nothing moves.
     */
    private revealSuggestedGroup(framesLeft = 10) {
        if (this.suggestionRevealState === 'done' || !this.props.suggestion || !this.extraProps || this.suggestedGroupIds.length === 0) return;
        this.suggestionRevealState = 'done';
        const nextFrame = (fn: () => void) => typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame(fn)
            : setTimeout(fn, 16);
        const attempt = (left: number) => {
            const body = this.bodyRef.current;
            if (!body || !body.isConnected) return;
            const group = body.querySelector<HTMLElement>('[data-suggested="true"]');
            if (group) {
                const choice = group.querySelector<HTMLElement>('[data-suggested-choice="true"]');
                revealInScrollParent(choice ?? group, { keep: body.querySelector<HTMLElement>('[data-jkey="reedit-suggestion-status"]') });
            } else if (left > 0) {
                nextFrame(() => attempt(left - 1));
            }
        };
        nextFrame(() => attempt(framesLeft));
    }

    // removeFile, orientationChanged, convertOptionCategoriesChanged removed (handled by Editor or Options component)

    convertOptionCategoriesChanged(summary: ConvertOptionsSummary) {
        // FIX-8 8.4: "the moment any option changes, the primary goes back to Apply & Restart".
        this.refreshRerun();
        this.forceUpdate();
    }

    /**
     * FIX-2 (UX-SPEC §2.1 T3, X2-3): the server says the upload is gone. The editor already forgot the refused card
     * without deleting anything; the card this modal was opened from and its output stay as they were. The modal swaps
     * Apply & Restart for "Upload it again" + "Close" and moves focus to the alert at the top of the body. No dialog.
     */
    onUploadExpired = (event: UploadExpiredEvent) => {
        // X2-3 says "Upload it again to use THESE settings": the refused card carries what the user just applied here.
        this.refusedOptions = event?.expired?.[0]?.fileUpload?.options || null;
        try {
            container.get(MetricsService).emit(MetricEventName.SCHEDULE_REFUSED_EXPIRED, {
                surface: 'reedit_modal', page: this.slug(), action: 'alert',
            });
        } catch { /* never load-bearing */ }
        // C6 slots: X2-3 in the alert at the top of the body (componentDidUpdate focuses it and scrolls it into
        // view) and the `expired` footer.
        this.setState({ expired: true, alert: { text: X2_COPY.modalAlert } });
    };

    private slug(): string {
        const fu = this.props.fileUpload;
        return `${fu?.pageData?.path || fu?.uniqueProcessType || ''}`;
    }

    /** The options of the card the server refused (the modal's edited settings); null before a refusal. */
    private refusedOptions: unknown = null;

    /**
     * X2-2 from the modal: the card's tool page with the settings the user just applied in this modal (the refused
     * card's, else the original card's), through the one-shot handoff; then the modal closes.
     */
    uploadAgain = () => {
        const fu = this.props.fileUpload;
        const href = prepareUploadAgain(fu && {
            pageData: fu.pageData, uniqueProcessType: fu.uniqueProcessType, originalInputFiles: fu.originalInputFiles,
            options: this.refusedOptions ?? fu.options,
        });
        try {
            container.get(MetricsService).emit(MetricEventName.EXPIRED_UPLOAD_AGAIN_CLICKED, { surface: 'reedit_modal', page: this.slug() });
        } catch { /* never load-bearing */ }
        this.hide();
        if (href) goToUploadAgain(href, (h) => Router.push(h));
    };

    componentWillUnmount() {
        // for untilDestroyed
        this.unmounted = true;
    }

    private converting = false;
    private unmounted = false;
    public async convert() {
        if (this.converting) {
            this.emitRerun(MetricEventName.CONVERT_DOUBLE_PRESS_SUPPRESSED);
            return;
        }
        if (this.state?.expired) return;
        this.converting = true;
        // FIX-8 8.2: "Starting…", disabled and aria-busy from the first press until the modal closes
        // (success hides it) or the request fails.
        if (this.rerunEnabled) this.forceUpdate();
        try {
            if (this.editorRef.current) {
                await this.editorRef.current.startConversion();
            }
        } finally {
            this.converting = false;
            if (this.rerunEnabled && !this.unmounted) this.forceUpdate();
        }
    }

    // ------------------------------------------------------- FIX-8 duplicate submissions (§8)

    /** `featureFlags.rerunDedupeOffer === true`; false until the settings say so (today's footer). */
    private rerunEnabled = false;
    private rerun: ReeditRerunState = NOT_UNCHANGED;
    private readonly rerunRegionId = nextRerunRegionId('reedit-rerun');

    private emitRerun(event: MetricEventName, attrs: Record<string, string> = {}) {
        try {
            container.get(MetricsService).emit(event, {
                surface: 'reedit',
                page: `${this.props.fileUpload?.pageData?.path || this.props.fileUpload?.uniqueProcessType || ''}`,
                ...attrs,
            });
        } catch { /* telemetry is never load-bearing */ }
    }

    /** UX-SPEC 8.1 for the modal: the same upload ids in order, the same tool, the same canonical options. */
    private computeRerun(): ReeditRerunState {
        const editor = this.editorRef.current;
        const ctx = this.extraProps?.convertOptionsContext;
        if (!this.rerunEnabled || !editor || !ctx) return NOT_UNCHANGED;
        try {
            return reeditRerunState({
                enabled: true,
                pending: () => editor.describeScheduleRequests(),
                cards: this.fileManagerService?.getFileUploads?.() || [],
                ctx: rerunContext(),
                defaults: ctx.getDefaultSummary(),
            }, typeof this.props.onDownloadExisting === 'function', {
                cardId: this.props.fileUpload?.id,
                canDownload: canDownloadExistingResult,
            });
        } catch (e) {
            // A check that throws must never stand between the user and Apply & Restart.
            this.logger.error({ message: 'Re-run check threw', error: e });
            return NOT_UNCHANGED;
        }
    }

    private refreshRerun() {
        if (this.unmounted) return;
        const next = this.computeRerun();
        const prev = this.rerun;
        if (next.running === prev.running && next.unchangedSucceeded === prev.unchangedSucceeded && next.prior?.id === prev.prior?.id) return;
        this.rerun = next;
        // Not while this modal's own Apply is starting: the card it just created matches by construction (fu-t10 F2).
        if (!this.converting && ((next.running && !prev.running) || (next.unchangedSucceeded && !prev.unchangedSucceeded))) {
            this.emitRerun(MetricEventName.RERUN_UNCHANGED_DETECTED, { prev: next.running ? 'running' : 'succeeded' });
        }
        this.forceUpdate();
    }

    private downloadResult = () => {
        this.emitRerun(MetricEventName.RERUN_UNCHANGED_DOWNLOAD_CLICKED);
        const prior = this.rerun.prior;
        if (prior && prior.id !== this.props.fileUpload?.id) {
            // fu-t10 F1: the matching run is a NEWER card than the one this modal was opened from (only offered when
            // its one-file result can be fetched, `reeditRerunState`): ITS result, through the card Download path.
            downloadExistingResult(prior);
        } else {
            // The card's own Download path, inside this click: it counts as the card's download.
            this.props.onDownloadExisting?.();
        }
        this.hide();
    };

    private runAnyway = () => {
        this.emitRerun(MetricEventName.RERUN_UNCHANGED_RUN_ANYWAY);
        this.convert();
    };

    /**
     * The footer: its wrapper for [footerMode], the buttons from [renderFooter]. Flag off, and the default mode,
     * keep today's wrapper (`<div data-modalfooter>`); FIX-8's two explanatory footers carry their own layout.
     */
    private getFooter() {
        const mode = this.footerMode();
        if (mode === 'running' || mode === 'unchanged-success') {
            return <div data-modalfooter className={styles['rerun-footer']}
                data-jkey={mode === 'running' ? 'reedit-footer-running' : 'reedit-footer-unchanged'}>
                {this.renderFooter(mode)}
            </div>;
        }
        return <div data-modalfooter>{this.renderFooter(mode)}</div>;
    }



    hide() {
        this.modalRef && this.modalRef.current && this.modalRef.current.unmount();
    }

    // ---- prod fixes 2026-10-02 (PLAN.md C6): slots the fixes fill; unused, the modal is today's -------------

    /** Shows (or with null, removes) the inline refusal at the top of the body. */
    showAlert(alert: ReeditAlert | null) {
        this.setState({ alert });
    }

    /** Sets (or with null, clears) the polite status line above the options. */
    setStatusLine(statusLine: string | null) {
        this.setState({ statusLine });
    }

    /** FIX-2 (T3): the upload is gone -- the footer switches to its `expired` mode. */
    setExpired(expired: boolean) {
        this.setState({ expired });
    }

    componentDidUpdate() {
        // FIX-8 (T10): the editor's ref is only set once the editor has rendered, so the first re-run answer comes here.
        this.refreshRerun();
        // FIX-3b (UX1-07): once the options are on screen, show the suggested setting (once per modal).
        this.revealSuggestedGroup();
        // A refusal of what the user just did takes focus, once per alert, and is scrolled into view
        // (UX-SPEC section 1: the alert sits at the top of the modal body; on 390 px it would be off-screen).
        const alert = this.state?.alert ?? null;
        if (alert && alert !== this.focusedAlert && this.alertRef.current) {
            this.focusedAlert = alert;
            try {
                this.alertRef.current.focus();
                this.alertRef.current.scrollIntoView?.({ block: 'nearest' });
            } catch { /* focus is best effort */ }
        }
        if (!alert) this.focusedAlert = null;
    }

    /**
     * The footer mode (`reedit-footer-mode.ts`): expired > running > unchanged-success > default. `running` and
     * `unchangedSucceeded` are FIX-8's (T10, `featureFlags.rerunDedupeOffer`; flag off = always `default` unless
     * expired). While the first Apply press is starting, the default footer shows "Starting…" instead.
     */
    footerMode(): ReeditFooterMode {
        const rerunLive = this.rerunEnabled && !this.converting;
        return resolveReeditFooterMode({
            expired: this.state?.expired === true,
            running: rerunLive && this.rerun.running,
            unchangedSucceeded: rerunLive && this.rerun.unchangedSucceeded,
            suggestion: this.props.suggestion,
        });
    }

    renderAlert() {
        const alert = this.state?.alert;
        if (!alert) return null;
        return <div ref={this.alertRef} role="alert" tabIndex={-1} className={`alert alert-warning ${styles['reedit-alert'] ?? ''}`} data-jkey="reedit-alert">
            <div>{alert.text}</div>
            {alert.actions && alert.actions.length > 0 && <div className={styles['reedit-alert-actions']}>
                {alert.actions.map((a, i) => (
                    <button key={i} type="button" className={`${styles['btn']} ${a.primary ? styles['btn-primary'] : ''}`} onClick={() => a.onClick()}>{a.label}</button>
                ))}
            </div>}
        </div>;
    }

    /**
     * The footer for [mode]: `default` is Apply & Restart + Cancel, exactly as before (with FIX-8 on: "Starting…",
     * disabled and aria-busy from the first press); `expired` is T3's (FIX-2); `running` / `unchanged-success` are
     * T10's (FIX-8, UX-SPEC 8.3/8.4).
     */
    renderFooter(mode: ReeditFooterMode) {
        const helpId = `${this.rerunRegionId}-help`;
        const cancel = <button className={styles['btn']} onClick={() => this.hide()}>Cancel</button>;
        switch (mode) {
            case 'expired':
                // FIX-2 X2-3 (T3): Apply & Restart is gone; "Upload it again" (primary) + "Close".
                return <>
                    <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={this.uploadAgain}
                        data-jkey="reedit-upload-again">{X2_COPY.uploadAgain}</button>
                    <button className={styles['btn']} onClick={() => this.hide()} data-jkey="reedit-close">{X2_COPY.close}</button>
                </>;
            case 'unchanged-success':
                return <>
                    <p className={styles['rerun-footer-helper']} id={helpId}>{rerunCopy('X8-5')}</p>
                    <div className={styles['rerun-footer-actions']}>
                        <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={this.downloadResult}
                            aria-describedby={helpId} data-jkey="reedit-download-result">{rerunCopy('X8-6')}</button>
                        {cancel}
                        <button type="button" className={styles['btn-link']} onClick={this.runAnyway}
                            data-jkey="reedit-run-anyway">{rerunCopy('X8-7')}</button>
                    </div>
                </>;
            case 'running':
                return <>
                    <p className={styles['rerun-footer-helper']} id={helpId}>{rerunCopy('X8-4')}</p>
                    <div className={styles['rerun-footer-actions']}>
                        {/* fu-t10 F3: aria-disabled, not `disabled` -- a disabled key cannot take focus, so a keyboard or
                            screen-reader user never reached it or heard X8-4 (UX-SPEC section 1). A press does nothing. */}
                        <button type="button" className={`${styles['btn']} ${styles['btn-primary']}`} aria-disabled={true}
                            aria-describedby={helpId} data-jkey="reedit-apply">Apply &amp; Restart</button>
                        {cancel}
                    </div>
                </>;
            default: {
                if (!this.rerunEnabled) {
                    return <>
                        <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={() => this.convert()}>Apply &amp; Restart</button>
                        <button className={styles['btn']} onClick={() => this.hide()}>Cancel</button>
                    </>;
                }
                const starting = this.converting;
                return <>
                    {/* fu-t10 F3 (as UX3-03 on the tool page): aria-disabled keeps the key's focus while it starts and
                        after a failure; `convert()` drops a second press. */}
                    <button className={`${styles['btn']} ${styles['btn-primary']}`} onClick={() => this.convert()}
                        aria-disabled={starting ? true : undefined} aria-busy={starting ? true : undefined} data-jkey="reedit-apply">
                        {starting ? rerunCopy('X8-1') : <>Apply &amp; Restart</>}
                    </button>
                    {cancel}
                </>;
            }
        }
    }

    updateProcessProbedDataFn() {
        // Delegated
        return (async () => { }) as any;
    }


    /**
     * FIX-3b (UX-SPEC 3.3, X3-B2): "We've picked a stronger setting: {label}. You can change it." above the options. The
     * polite region is mounted empty with the modal and filled once the options (with the suggestion applied) are on
     * screen, so a screen reader announces it then -- and never claims a setting the editor is not showing yet.
     */
    getSuggestionStatusLine() {
        const suggestion = this.props.suggestion;
        if (!suggestion) return null;
        return <div className={styles['suggestion-status']} role="status" data-jkey="reedit-suggestion-status">
            {this.extraProps ? fmtOutcomeCopy(COMPRESS_OUTCOME_COPY['X3-B2'], { label: suggestion.label }) : ''}
        </div>;
    }

    render() {
        return <Modal ref={this.modalRef} modalClass={styles['modal']}>
            <div data-modalheader>
                <h4 className="modal-title">Edit Conversion Settings</h4>
            </div>
            <div className="modal-body contact-us-modal" data-modalbody ref={this.bodyRef}>
                {this.renderAlert()}
                {/* FIX-3b's X3-B2 line opens the body (after an alert); C6's generic status slot follows, empty until used. */}
                {this.getSuggestionStatusLine()}
                <div role="status" data-jkey="reedit-status-line" className={this.state?.statusLine ? styles['reedit-status-line'] : undefined}>{this.state?.statusLine || ''}</div>
                {
                    this.uploadInProgressFiles && this.extraProps && <div className="mt-3 mb-3">
                        <FileProcessEditorComponent
                            ref={this.editorRef}
                            initialFiles={this.uploadInProgressFiles}
                            convertOptionsContext={this.extraProps.convertOptionsContext}
                            toType={this.props.fileUpload.toType}
                            fromType={this.props.fileUpload.fromType}
                            processType={this.props.fileUpload.processType}
                            pageData={this.props.fileUpload.pageData}
                            onConversionSuccess={() => this.hide()}
                            onUploadExpired={this.onUploadExpired}
                            hideRemoveButton={true}
                        />
                    </div>
                }
                {
                    this.extraProps && <AdvancedOptionsSectionComponent
                        convertOptionsContext={this.extraProps.convertOptionsContext}
                        uploadInProgressFiles={this.extraProps.uploadInProgressFiles}
                        onOptionChange={this.extraProps.onOptionChange}
                        path={this.props.fileUpload.uniqueProcessType}
                        suggestion={this.props.suggestion ? { groupIds: this.suggestedGroupIds, propertyName: this.props.suggestion.optionKey } : undefined}
                    />
                }
            </div>
            {this.getFooter()}
        </Modal>
    }
}

